import {
  Button, Card, DateInput, Select, TextInput,
} from 'nhsuk-react-components';
import { ChangeEvent, useState } from 'react';
import type { DateInputChangeEvent, DateInputValue } from 'nhsuk-react-components';
import { ZodError } from 'zod';
import {
  CampaignDetails,
  CampaignConfig,
  campaignDetailsFormSchema,
  CampaignTarget,
} from 'schemas/CampaignConfig.schema.ts';
import {
  CAMPAIGN_TARGETS,
  CAMPAIGN_TYPES,
  CampaignType,
} from 'schemas/enums/campaigns.ts';
import {
  campaignTypeToLabel,
  labelToUnixDate,
  labelToYYYYMMDD,
  validateDateLengths,
  YYYYMMDDToLabel,
} from 'utils/formattingUtils.ts';
import 'components/forms/CampaignDetails/CampaignDetailsForm.scss';
import CampaignPageHeader from 'components/CampaignPageHeader';
import SelectCampaignTarget from 'components/forms/CampaignDetails/SelectCampaignTarget.tsx';
import { getCampaignContent } from 'utils/contentUtils.ts';
import { CampaignTemplate } from 'schemas/CampaignTemplates.schema.ts';

const parseInitialValues = (campaign: CampaignConfig): CampaignDetails => ({
  Name: campaign.CampaignConfig?.Name || '',
  Type: campaign.CampaignConfig?.Type || CAMPAIGN_TYPES[0],
  Target: campaign.CampaignConfig?.Target || CAMPAIGN_TARGETS.V[0],
  StartDate: campaign.CampaignConfig?.StartDate || '',
  EndDate: campaign.CampaignConfig?.EndDate || '',
});

function CampaignDetailsForm({
  onSuccessfulSubmission,
  isSubmitting = false,
  campaign,
  template,
}: {
    onSuccessfulSubmission: (campaignDetails: CampaignDetails) => void;
    isSubmitting?: boolean;
    campaign: CampaignConfig;
    template?: CampaignTemplate,
}) {
  const [errors, setErrors] = useState<Partial<{ [key in keyof CampaignDetails]: string; }>>({});
  const [formValues, setFormValues] = useState<CampaignDetails>(
    parseInitialValues(campaign),
  );
  const [dates, setDates] = useState({
    StartDate: YYYYMMDDToLabel(campaign.CampaignConfig?.StartDate) || {
      day: '',
      month: '',
      year: '',
    },
    EndDate: YYYYMMDDToLabel(campaign.CampaignConfig?.EndDate) || {
      day: '',
      month: '',
      year: '',
    },
  });

  const campaignContent = getCampaignContent(formValues.Type);

  function clearErrorsForField(fieldName: keyof CampaignDetails) {
    if (errors[fieldName]) {
      const clearedErrors = { ...errors };
      delete clearedErrors[fieldName];
      setErrors(clearedErrors);
    }
  }

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    clearErrorsForField(e.target.name as keyof CampaignDetails);
    setFormValues({
      ...formValues,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChangeType = (e: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({
      ...formValues,
      Type: e.target.value as CampaignType,
      Target: CAMPAIGN_TARGETS[e.target.value as CampaignType][0],
    });
  };

  const handleSelectChangeTarget = (e: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({
      ...formValues,
      Target: e.target.value as CampaignTarget,
    });
  };

  const handleDateChange = (
    e: DateInputChangeEvent,
    Name: 'StartDate' | 'EndDate',
  ) => {
    const { day = '', month = '', year = '' } = (e.target.value || {}) as Partial<DateInputValue>;
    const typedValue = { day, month, year };
    clearErrorsForField(Name);
    setDates({
      ...dates,
      [Name]: typedValue,
    });

    const errorMessage = validateDateLengths(typedValue);
    if (errorMessage) {
      setErrors({
        ...errors,
        [Name]: errorMessage,
      });
    } else {
      clearErrorsForField(Name);
    }
  };

  const onSubmit = () => {
    try {
      setErrors({});

      const YYYYMMDDStartDate = labelToYYYYMMDD(dates.StartDate);
      const YYYYMMDDEndDate = labelToYYYYMMDD(dates.EndDate);
      const submittedFormValues = {
        ...formValues,
        StartDate: YYYYMMDDStartDate,
        EndDate: YYYYMMDDEndDate,
      };

      setFormValues(submittedFormValues);

      campaignDetailsFormSchema.parse(submittedFormValues);

      if (labelToUnixDate(dates.StartDate) > labelToUnixDate(dates.EndDate)) {
        setErrors({
          ...errors,
          EndDate: 'End date must be after start date',
        });

        return;
      }

      const iterationBeforeStartDate = (
        campaign.CampaignConfig.Iterations ?? []
      ).find((i) => {
        if (i.IterationDate) {
          return i.IterationDate < YYYYMMDDStartDate;
        }

        return false;
      });
      if (iterationBeforeStartDate) {
        setErrors({
          ...errors,
          StartDate: `The iteration run date for ${iterationBeforeStartDate.Name} is earlier than the campaign start date. Update the campaign dates or the iteration run date to continue.`,
        });

        return;
      }

      const iterationAfterEndDate = (
        campaign.CampaignConfig.Iterations ?? []
      ).find((i) => {
        if (i.IterationDate) {
          return i.IterationDate > YYYYMMDDEndDate;
        }

        return false;
      });
      if (iterationAfterEndDate) {
        setErrors({
          ...errors,
          EndDate: `The iteration run date for ${iterationAfterEndDate.Name} is later than the campaign end date. Update the campaign dates or the iteration run date to continue.`,
        });

        return;
      }

      onSuccessfulSubmission(submittedFormValues);
    } catch (e) {
      if (e instanceof ZodError) {
        setErrors(Object.fromEntries(
          e.issues.map(({ path, message }) => [path[0].toString(), message]),
        ));
      }
    }
  };

  return (
    <>
      <CampaignPageHeader campaign={campaign} template={template} />
      <Card>
        <>
          <h3>Set campaign details</h3>
          <div className="form-field-group">
            {
              template?.['CampaignConfig.Type.input.display'] === 'hidden'
                ? null : (
                  <Select
                    id="campaign-type"
                    label="Choose a campaign type"
                    name="Type"
                    onChange={handleSelectChangeType}
                    value={formValues.Type}
                    hint={template?.['CampaignConfig.Type.input.hint']}
                  >
                    {CAMPAIGN_TYPES.map((campaignType) => (
                      <Select.Option value={campaignType} key={campaignType}>
                        {campaignTypeToLabel(campaignType)}
                      </Select.Option>
                    ))}
                  </Select>
                )
            }

            {
              template?.['CampaignConfig.Target.input.display'] === 'hidden'
                ? null : (
                  <SelectCampaignTarget
                    handleSelectChange={handleSelectChangeTarget}
                    formValues={formValues}
                  />
                )
            }
          </div>
          <div className="form-field-group">
            <h4>
              {campaignContent.name}
            </h4>
            <TextInput
              id="campaign-name"
              label={campaignContent.description}
              hint={campaignContent.example}
              name="Name"
              onChange={handleTextChange}
              value={formValues.Name}
              error={errors.Name}
            />
          </div>
          {
            (
              template?.['CampaignConfig.StartDate.input.display'] !== 'hidden'
              || template?.['CampaignConfig.EndDate.input.display'] !== 'hidden'
            ) && (
              <h4>{campaignContent.startAndEndDateIntro}</h4>
            )
          }

          <div className="form-field-group">
            {
              template?.['CampaignConfig.StartDate.input.display'] === 'hidden'
                ? null : (
                  <DateInput
                    id="campaign-start-date"
                    legend={campaignContent.startDateLabel}
                    onChange={(e) => handleDateChange(e, 'StartDate')}
                    value={dates.StartDate}
                    error={errors.StartDate}
                  />
                )
            }
            {
              template?.['CampaignConfig.EndDate.input.display'] === 'hidden'
                ? null : (
                  <DateInput
                    id="campaign-end-date"
                    legend={campaignContent.endDateLabel}
                    onChange={(e) => handleDateChange(e, 'EndDate')}
                    value={dates.EndDate}
                    error={errors.EndDate}
                  />
                )
            }
          </div>
        </>
      </Card>

      <Button onClick={onSubmit} disabled={isSubmitting}>
        {isSubmitting ? 'Updating...' : 'Save and continue'}
      </Button>
    </>
  );
}

export default CampaignDetailsForm;
